import { AppLayoutHeaderWrapper } from "@src/appV2/redesign/AppLayout/HeaderWrapper";
import { AppLayoutInnerWrapper } from "@src/appV2/redesign/AppLayout/InnerWrapper";
import { NavigationMenuItem } from "@src/appV2/redesign/AppLayout/Navigation/MenuItem";
import { NavigationMenuItemsWrapper } from "@src/appV2/redesign/AppLayout/Navigation/MenuItemsWrapper";
import { NavigationMenuSectionTitle } from "@src/appV2/redesign/AppLayout/Navigation/MenuSectionTitle";
import { AppLayoutPageWrapper } from "@src/appV2/redesign/AppLayout/PageWrapper";
import { AppLayoutWrapper } from "@src/appV2/redesign/AppLayout/Wrapper";
import { logout } from "@src/modules/session";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/utils/routes";
import { Modal } from "antd";
import firebase from "firebase/app";
import { useEffect } from "react";
import { Redirect, Route, useHistory } from "react-router-dom";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { AdminNavigationMenuItemsContainer } from "./AdminNavigationMenuItemsContainer";
import { WorkerNavigationMenuItemsContainer } from "./WorkerNavigationMenuItemsContainer";
import { WorkplaceNavigationMenuItemsContainer } from "./WorkplaceNavigationMenuItemsContainer";
import { AdminMenuPriviligesProvider } from "./priviligesProviders/AdminMenuPriviligesProvider";
import { WorkplaceMenuPriviligesProvider } from "./priviligesProviders/WorkplaceMenuPriviligesProvider";
import { AdminRoutes } from "./AdminRoutes";
import { WorkplaceRoutes } from "./WorkplaceRoutes";

interface NavigationMenuContainerProps {
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const FirebaseLoginVerify = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.FirebaseLoginVerify,
    }))
);

function logoutConfirm() {
  Modal.confirm({
    title: "Are you sure you want to logout?",
    onOk: logout,
  });
}

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  const { isAdmin, isAgent, isFacility: isWorkplace } = props;

  const history = useHistory();

  // redirect to the login page, if the user is not authenticated and not use a verification link
  useEffect(() => {
    const currentPath = window.location.pathname;

    return firebase.auth().onAuthStateChanged((user) => {
      // Skip redirect during the email verification process
      const isVerificationPage = currentPath.includes("login/verify");

      if (!user && !isVerificationPage) {
        history.replace("/login");
      }
    });
  }, [history]);

  const renderNavigationMenu = () => {
    if (isAdmin) {
      return <AdminNavigationMenuItemsContainer />;
    }

    if (isAgent) {
      return <WorkerNavigationMenuItemsContainer />;
    }

    if (isWorkplace) {
      return <WorkplaceNavigationMenuItemsContainer />;
    }

    return null;
  };

  function renderStartPage() {
    if (isAdmin) {
      return <Redirect to={routes.admin} />;
    }

    if (isAgent) {
      return <Redirect to={routes.agentDashboard} />;
    }

    if (isWorkplace) {
      return <Redirect to={routes.agentDashboard} />;
    }

    return null;
  }

  return (
    <BerlinThemeProvider>
      <AdminMenuPriviligesProvider>
        <WorkplaceMenuPriviligesProvider isWorkplace={isWorkplace}>
          <Route exact path={routes.login} component={AdminAppLogin} />

          <Route exact path={routes.loginVerify} component={FirebaseLoginVerify} />

          <AppLayoutWrapper>
            <AppLayoutHeaderWrapper>Test</AppLayoutHeaderWrapper>
            <AppLayoutInnerWrapper>
              <NavigationMenuItemsWrapper>
                {renderNavigationMenu()}
                <NavigationMenuSectionTitle>Logout</NavigationMenuSectionTitle>
                <NavigationMenuItem
                  iconType="door-straight"
                  isSelected={false}
                  onClick={() => {
                    logoutConfirm();
                  }}
                >
                  Logout
                </NavigationMenuItem>
              </NavigationMenuItemsWrapper>
              <AppLayoutPageWrapper>
                <Route exact path="/" render={() => renderStartPage()} />

                <AdminRoutes />

                <WorkplaceRoutes isAgent={isAgent} />
              </AppLayoutPageWrapper>
            </AppLayoutInnerWrapper>
          </AppLayoutWrapper>
        </WorkplaceMenuPriviligesProvider>
      </AdminMenuPriviligesProvider>
    </BerlinThemeProvider>
  );
}
